import { inject, Injectable } from '@angular/core';
import { Auth, authState, User } from '@angular/fire/auth';
import { Observable } from 'rxjs';
import { filter, map, distinctUntilChanged } from 'rxjs';

/**
 * AuthService - Wrapper around Firebase Auth
 *
 * Provides a testable abstraction over Firebase Authentication.
 * This service can be easily mocked in tests to provide controlled user state.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly auth = inject(Auth);

  /**
   * Observable of the current authenticated user
   * Filters out null/undefined users - only emits when user is authenticated
   */
  public readonly user$: Observable<User> = authState(this.auth).pipe(
    filter((user): user is User => !!user),
  );

  /**
   * Observable of the current user's UID
   * Distinct until changed to prevent unnecessary emissions
   */
  public readonly uid$: Observable<string> = this.user$.pipe(
    map((user) => user.uid),
    distinctUntilChanged(),
  );

  /**
   * Get the current user synchronously
   * Returns null if no user is authenticated
   */
  get currentUser(): User | null {
    return this.auth.currentUser;
  }
}
